import { create } from 'zustand';
import { api, onSessionExpired, refreshAccessToken, setAccessToken } from '../lib/api';
import type { Branding, PublicUser } from '../lib/types';
import { useWorkspace } from './workspace';

interface AuthState {
  user: PublicUser | null;
  organization: Branding | null;
  /** False until the initial refresh attempt settles, so routes don't flash. */
  ready: boolean;
  signIn: (
    identifier: string,
    password: string,
    organizationSlug?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
  setOrganization: (org: Branding) => void;
}

/** The one in-flight restore, so concurrent callers share a single exchange. */
let restoring: Promise<void> | null = null;

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  organization: null,
  ready: false,

  async signIn(identifier, password, organizationSlug) {
    const { data } = await api.post<{ accessToken: string; user: PublicUser }>('/auth/login', {
      identifier,
      password,
      ...(organizationSlug ? { organizationSlug } : {}),
    });
    setAccessToken(data.accessToken);
    set({ user: data.user });
    await get().restore();
  },

  async signOut() {
    await api.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    // The centre an administrator had open goes with them. Otherwise the next
    // person to sign in on this machine lands inside somebody else's, which is
    // both confusing and more than they were meant to see.
    useWorkspace.getState().forget();
    set({ user: null, organization: null });
  },

  /**
   * Rebuilds the session from the refresh cookie on a cold load, then fetches
   * the profile — which is also where an instructor's branding arrives, so the
   * console paints in their colours rather than the platform default.
   *
   * Shared rather than re-entered: React runs mount effects twice in
   * development, and two restores would otherwise present the same one-use
   * cookie twice and lose the session to its own replay detection.
   */
  restore() {
    restoring ??= (async () => {
      try {
        if (!get().user) {
          const token = await refreshAccessToken();
          if (!token) throw new Error('no session');
        }
        const me = await api.get<{ user: PublicUser; organization: Branding | null }>('/auth/me');
        set({ user: me.data.user, organization: me.data.organization });
      } catch {
        setAccessToken(null);
        set({ user: null, organization: null });
      } finally {
        set({ ready: true });
        restoring = null;
      }
    })();
    return restoring;
  },

  setOrganization(org) {
    set({ organization: org });
  },
}));

onSessionExpired(() => {
  setAccessToken(null);
  useAuth.setState({ user: null, organization: null });
});
